import React, { useRef, useEffect, useMemo } from "react";
import styles from "./styles.module.scss";
import restartIcon from "../../../components/SVGcomponents/restart-icon.svg";
import { KeywordsOrder, BaseOrder, OrderType, RatingsOrder } from "./types";
import Spinner from "../../Spinner";
import KeywordOrderDetails from "./KeywordOrderDetails";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { utcTimezones, TimezoneKey } from "../../../utils/constants";

dayjs.extend(utc);
dayjs.extend(timezone);

// Функция для конвертации UTC offset в IANA timezone
const getIANATimezone = (timezone: string): string => {
  // Если timezone уже в формате IANA, возвращаем как есть
  if (timezone.includes('/')) {
    return timezone;
  }

  // Если это UTC offset, конвертируем в IANA
  if (timezone.startsWith('UTC')) {
    // Проверяем, есть ли такой ключ в utcTimezones
    if (utcTimezones[timezone as TimezoneKey]) {
      return utcTimezones[timezone as TimezoneKey];
    }

    // Если нет, пробуем преобразовать формат
    const offset = timezone.replace('UTC', '').trim();
    const timezoneKey = `UTC${offset}` as TimezoneKey;
    if (utcTimezones[timezoneKey]) {
      return utcTimezones[timezoneKey];
    }
  }

  // По умолчанию возвращаем UTC
  return 'Etc/UTC';
};

interface ProgressItem {
  date: string;
  status: string;
  visits: {
    current: number;
    total: number;
    stars: string;
    percent: number;
    expectedCount: number;
  };
  fiveStars: {
    current: number;
    total: number;
    stars: number;
    percent: number;
    expectedCount: number;
  };
  fourStars: {
    current: number;
    total: number;
    stars: number;
    percent: number;
    expectedCount: number;
  };
}

interface OrderDetailsProps {
  loading: boolean;
  order: OrderType;
}

interface RatingsDay {
  date: string;
  fourStarRating: number;
  fiveStarRating: number;
}

interface Review {
  reviewDate: string;
  reviewEvaluation: "FOUR_STARS" | "FIVE_STARS";
  reviewText: string;
  username: string;
  status: string;
}

interface DateInstall {
  date: string;
  installs: number;
}

interface ReviewsOrder extends BaseOrder {
  __typename: "ReviewsOrders";
  reviews: Review[];
}

type OrderData = RatingsOrder | ReviewsOrder | KeywordsOrder;

interface UserOrder {
  orderId: number;
  status: string;
  orderCompletion: number;
  orderPrice: number;
  initialDate: string;
  endDate: string;
  createdAt: string;
  isFavorite: boolean;
  orderType: string;
  app: {
    title: string;
    icon: string;
    country: string;
    store: string;
  };
  reviews?: Review[];
  ratingsPerDay?: RatingsDay[];
}

const StarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M7.07294 1.29235C7.41 0.458908 8.59 0.458908 8.92706 1.29235L10.3639 4.84528C10.5072 5.1995 10.8397 5.44113 11.2209 5.46791L15.0439 5.73652C15.9407 5.79953 16.3054 6.92178 15.6169 7.49989L12.6819 9.96434C12.3892 10.21 12.2622 10.601 12.3545 10.9718L13.2804 14.6907C13.4977 15.5631 12.543 16.2567 11.7804 15.7805L8.52964 13.7507C8.20554 13.5483 7.79446 13.5483 7.47036 13.7507L4.21957 15.7805C3.45699 16.2567 2.50235 15.5631 2.71955 14.6907L3.64547 10.9718C3.73779 10.601 3.61075 10.21 3.31814 9.96434L0.383116 7.49989C-0.305382 6.92178 0.0592597 5.79953 0.956071 5.73652L4.77912 5.46791C5.16027 5.44113 5.49284 5.1995 5.63609 4.84528L7.07294 1.29235Z"
      fill="var(--ui-text-secondary-default)"
    />
  </svg>
);

const CancelIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
  >
    <path
      d="M9.5 3.50005C9.5 1.8432 8.15687 0.500062 6.50002 0.500048L3.50008 0.500024C1.84322 0.500011 0.500066 1.84314 0.500052 3.5L0.500026 6.49997C0.500012 8.15684 1.84316 9.5 3.50003 9.5L6.5 9.5C8.15685 9.5 9.5 8.15685 9.5 6.5V3.50005ZM7.51531 6.98469C7.55015 7.01953 7.57779 7.06089 7.59665 7.10641C7.6155 7.15194 7.62521 7.20073 7.62521 7.25C7.62521 7.29927 7.6155 7.34806 7.59665 7.39359C7.57779 7.43911 7.55015 7.48047 7.51531 7.51531C7.48047 7.55015 7.43911 7.57779 7.39359 7.59665C7.34806 7.6155 7.29927 7.62521 7.25 7.62521C7.20073 7.62521 7.15194 7.6155 7.10641 7.59665C7.06089 7.57779 7.01953 7.55015 6.98469 7.51531L5 5.53016L3.01531 7.51531C2.94495 7.58568 2.84951 7.62521 2.75 7.62521C2.65049 7.62521 2.55505 7.58568 2.48469 7.51531C2.41432 7.44495 2.37479 7.34951 2.37479 7.25C2.37479 7.15049 2.41432 7.05505 2.48469 6.98469L4.46984 5L2.48469 3.01531C2.41432 2.94495 2.37479 2.84951 2.37479 2.75C2.37479 2.65049 2.41432 2.55505 2.48469 2.48469C2.55505 2.41432 2.65049 2.37479 2.75 2.37479C2.84951 2.37479 2.94495 2.41432 3.01531 2.48469L5 4.46984L6.98469 2.48469C7.05505 2.41432 7.15049 2.37479 7.25 2.37479C7.34951 2.37479 7.44495 2.41432 7.51531 2.48469C7.58568 2.55505 7.62521 2.65049 7.62521 2.75C7.62521 2.84951 7.58568 2.94495 7.51531 3.01531L5.53016 5L7.51531 6.98469Z"
      fill="var(--ui-text-primary-error)"
    />
  </svg>
);

const DoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 0C3.584 0 0 3.584 0 8C0 12.416 3.584 16 8 16C12.416 16 16 12.416 16 8C16 3.584 12.416 0 8 0ZM6.4 12L2.4 8L3.528 6.872L6.4 9.736L12.472 3.664L13.6 4.8L6.4 12Z"
      fill="var(--success-foreground-strong)"
    />
  </svg>
);

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn("formatDate: некорректная дата", dateString);
    return "??.??";
  }
  return `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}`;
};

const formatDateWithYear = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn("formatDateWithYear: некорректная дата", dateString);
    return "??.??.??";
  }
  return `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}.${date.getFullYear().toString().slice(-2)}`;
};

const formatPrice = (price: number) => {
  return price.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const getOrderTypeDisplay = (orderType: string) => {
  switch (orderType?.toLowerCase()) {
    case "ratings":
      return "Ratings";
    case "reviews":
      return "Reviews";
    case "keywords":
      return "Keywords";
    default:
      return orderType;
  }
};

export function getExpectedCountToday(
  date: string,
  totalPerDay: number,
  timezone: string,
): { percent: number; expectedCount: number } {
  if (!date || typeof totalPerDay !== "number" || isNaN(totalPerDay)) {
    console.warn("getExpectedCountToday: пустая дата или totalPerDay", { date, totalPerDay, timezone });
    return { percent: 0, expectedCount: 0 };
  }

  const ianaTimezone = getIANATimezone(timezone);

  let startDate, endDate;
  let startStr = '', endStr = '';
  if (date.includes("-")) {
    // YYYY-MM-DD или ISO
    const dateOnly = date.split('T')[0];
    startStr = `${dateOnly}T00:00`;
    endStr = `${dateOnly}T23:59`;
    console.log("getExpectedCountToday: YYYY-MM-DD", { startStr, endStr, ianaTimezone });
    startDate = dayjs.tz(startStr, ianaTimezone);
    endDate = dayjs.tz(endStr, ianaTimezone);
  } else if (date.includes("/")) {
    // DD/MM/YYYY
    const [day, month, year] = date.split("/");
    if (!day || !month || !year) {
      console.warn("getExpectedCountToday: некорректная дата (DD/MM/YYYY)", { date, totalPerDay, timezone });
      return { percent: 0, expectedCount: 0 };
    }
    startStr = `${year}-${month}-${day}T00:00`;
    endStr = `${year}-${month}-${day}T23:59`;
    console.log("getExpectedCountToday: DD/MM/YYYY", { startStr, endStr, ianaTimezone });
    startDate = dayjs.tz(startStr, ianaTimezone);
    endDate = dayjs.tz(endStr, ianaTimezone);
  } else {
    console.warn("getExpectedCountToday: неизвестный формат даты", { date, totalPerDay, timezone });
    return { percent: 0, expectedCount: 0 };
  }

  if (!startDate.isValid() || !endDate.isValid()) {
    console.warn("getExpectedCountToday: невалидная дата после dayjs", { date, startDate: startDate.toString(), endDate: endDate.toString(), ianaTimezone });
    return { percent: 0, expectedCount: 0 };
  }

  const now = dayjs().tz(ianaTimezone);
  if (now.isBefore(startDate)) return { percent: 0, expectedCount: 0 };
  if (now.isAfter(endDate)) return { percent: 100, expectedCount: totalPerDay };
  const totalDuration = endDate.diff(startDate, "milliseconds");
  const elapsed = now.diff(startDate, "milliseconds");
  const percent = (elapsed / totalDuration) * 100;
  const expectedCount = Math.floor((percent / 100) * totalPerDay);
  return {
    percent: Math.floor(percent),
    expectedCount,
  };
}

const OrderDetailsComponent: React.FC<OrderDetailsProps> = ({
  loading,
  order,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const progressItems = useMemo(() => {
    if (!order) return [];

    const orderData = order;

    if (orderData.orderType === "ratings") {
      const [day, month, year] = orderData.initialDate.split("/");
      const startDate = new Date(`${month}/${day}/${year}`);
      const [endDay, endMonth, endYear] = orderData.endDate.split("/");
      const endDate = new Date(`${endMonth}/${endDay}/${endYear}`);
      const currentDate = new Date();

      // Генерация фиктивных данных, если ratingsPerDay отсутствует
      let ratingsPerDay: RatingsDay[] = [];
      if (orderData.ratingsPerDay && orderData.ratingsPerDay.length > 0) {
        ratingsPerDay = orderData.ratingsPerDay;
      } else {
        // Генерируем фиктивные данные на основе дат заказа
        const dayDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dayDiff >= 0) {
          for (let i = 0; i <= dayDiff; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            ratingsPerDay.push({
              date: currentDate.toISOString().split('T')[0],
              fiveStarRating: 5, // Фиксированное значение, как на клиенте
              fourStarRating: 5
            });
          }
        }
      }

      // Create a map of ratings by date for quick lookup
      const ratingsByDate = new Map();
      ratingsPerDay.forEach((rating: RatingsDay) => {
        ratingsByDate.set(rating.date, {
          fourStars: rating.fourStarRating,
          fiveStars: rating.fiveStarRating,
          total: rating.fourStarRating + rating.fiveStarRating,
        });
      });

      // Calculate daily targets (sum of amount of ratings per day)
      const DAILY_TARGET = 24;

      const dates = Array.from(ratingsByDate.entries())
        .filter(([dateStr]) => !!dateStr && !isNaN(new Date(dateStr).getTime()))
        .map(([dateStr, counts]) => {
          return {
            date: dateStr,
            status: orderData.status,
            visits: {
              current: getExpectedCountToday(
                dateStr,
                counts.total,
                orderData.user.timezone,
              ).expectedCount,
              total: DAILY_TARGET,
              stars: "Total",
              percent: getExpectedCountToday(
                dateStr,
                counts.total,
                orderData.user.timezone,
              ).percent,
              expectedCount: counts.fiveStars + counts.fourStars,
            },
            fiveStars: {
              current: getExpectedCountToday(
                dateStr,
                counts.fiveStars,
                orderData.user.timezone,
              ).expectedCount,
              total: DAILY_TARGET,
              stars: 5,
              percent: getExpectedCountToday(
                dateStr,
                counts.fiveStars,
                orderData.user.timezone,
              ).percent,
              expectedCount: counts.fiveStars,
            },
            fourStars: {
              current: getExpectedCountToday(
                dateStr,
                counts.fourStars,
                orderData.user.timezone,
              ).expectedCount,
              total: DAILY_TARGET,
              stars: 5,
              percent: getExpectedCountToday(
                dateStr,
                counts.fourStars,
                orderData.user.timezone,
              ).percent,
              expectedCount: counts.fourStars,
            },
          };
        });

      return dates;
    }

    if (orderData.orderType === "reviews") {
      // Create a map to store reviews by date
      const reviewsByDate = new Map();

      // Process reviews if available
      if (orderData.reviews && orderData.reviews.length > 0) {
        // Group reviews by date
        orderData.reviews.forEach((review) => {
          const dateStr = review.reviewDate;
          if (!reviewsByDate.has(dateStr)) {
            reviewsByDate.set(dateStr, {
              fiveStars: 0,
              fourStars: 0,
              total: 0,
            });
          }

          const dateData = reviewsByDate.get(dateStr);
          dateData.total += 1;

          if (review.reviewEvaluation === "FIVE_STARS") {
            dateData.fiveStars += 1;
          } else if (review.reviewEvaluation === "FOUR_STARS") {
            dateData.fourStars += 1;
          }
        });
      }

      // Convert map to array of progress items
      const DAILY_TARGET = 24; // Target reviews per day
      const dates = Array.from(reviewsByDate.entries())
        .filter(([dateStr]) => !!dateStr && !isNaN(new Date(dateStr).getTime()))
        .map(([dateStr, counts]) => {
          console.log("counts", counts);
          const { percent, expectedCount } = getExpectedCountToday(
            dateStr,
            counts.total,
            orderData.user.timezone,
          );
          return {
            date: dateStr,
            status: orderData.status,
            visits: {
              current: getExpectedCountToday(
                dateStr,
                counts.total,
                orderData.user.timezone,
              ).expectedCount,
              total: DAILY_TARGET,
              stars: "Total",
              percent: getExpectedCountToday(
                dateStr,
                counts.total,
                orderData.user.timezone,
              ).percent,
              expectedCount: counts.fiveStars + counts.fourStars,
            },
            fiveStars: {
              current: getExpectedCountToday(
                dateStr,
                counts.fiveStars,
                orderData.user.timezone,
              ).expectedCount,
              total: DAILY_TARGET,
              stars: 5,
              percent: getExpectedCountToday(
                dateStr,
                counts.fiveStars,
                orderData.user.timezone,
              ).percent,
              expectedCount: counts.fiveStars,
            },
            fourStars: {
              current: getExpectedCountToday(
                dateStr,
                counts.fourStars,
                orderData.user.timezone,
              ).expectedCount,
              total: DAILY_TARGET,
              stars: 5,
              percent: getExpectedCountToday(
                dateStr,
                counts.fourStars,
                orderData.user.timezone,
              ).percent,
              expectedCount: counts.fourStars,
            },
          };
        });

      // Sort dates chronologically
      dates.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

      return dates;
    }

    return [];
  }, [order]);

  if (loading) {
    return (
      <div className={styles.modalOverlay}>
        <div ref={modalRef} className={styles.modal}>
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  const getProgressBarColors = (status: string) => {
    switch (status?.toUpperCase()) {
      case "LAUNCH":
      case "LAUNCHING":
        return "var(--status-01-background-strong)";
      case "ACTIVE":
      case "IN_PROGRESS":
        return "var(--status-03-background-strong)";
      case "DONE":
      case "COMPLETED":
        return "var(--success-background-strong)";
      case "CANCELED":
        return "var(--neutral-background-strong)";
      case "PAUSED":
        return "var(--status-02-background-strong)";
      default:
        return "var(--ds-color-brand)";
    }
  };

  const calculateProgress = (current: number, total: number) => {
    return Math.round((current / total) * 100);
  };

  const getStatusConfig = (status: string) => {
    switch (status?.toUpperCase()) {
      case "LAUNCH":
      case "LAUNCHING":
        return {
          text: "Launch",
          className: styles.statusLaunch,
        };
      case "ACTIVE":
      case "IN_PROGRESS":
        return {
          text: "Active",
          className: styles.statusActive,
        };
      case "DONE":
      case "COMPLETED":
        return {
          text: "Done",
          icon: <DoneIcon />,
          className: styles.statusDone,
        };
      case "CANCELED":
        return {
          text: "Canceled",
          className: styles.statusCanceled,
        };
      case "PAUSED":
        return {
          text: "Paused",
          className: styles.statusPaused,
        };
      default:
        return {
          text: status,
          className: styles.statusDefault,
        };
    }
  };

  const renderOrderContent = () => {
    if (!order) return null;

    switch (order.orderType?.toLowerCase()) {
      case "keywords":
      case "direct":
        return (
          <KeywordOrderDetails
            loading={loading}
            order={order as KeywordsOrder}
          />
        );
      case "ratings":
      case "reviews":
        return (
          <div className={styles.modalContent} style={{ padding: "24px" }}>
            <div className={styles.progressHeader}>
              <h3>Progress</h3>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0,
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    background: "var(--ui-border-secondary-default)",
                  }}
                />
                <div className={styles.actions}>
                  <button className={styles.restartButton}>
                    <img src={restartIcon} alt="Restart" />
                    Restart
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.progress}>
              <div className={styles.progressItems}>
                {progressItems.map((item: ProgressItem, index: number) => (
                  <div key={index} className={styles.progressItem}>
                    <div className={styles.dateColumn}>
                      {formatDateWithYear(item.date)}
                    </div>
                    <div className={styles.metricsColumn}>
                      <div
                        className={styles.progressBar}
                        style={{
                          background:
                            item.status === "PAUSED"
                              ? "var(--status-02-background-subtle)"
                              : "var(--success-background-subtle)",
                        }}
                      >
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${item.visits.percent}%`,
                            backgroundColor: getProgressBarColors(item.status),
                          }}
                        />
                        <span className={styles.progressValue}>
                          {item.visits.percent}%
                        </span>
                      </div>
                      <span className={styles.metricCount}>
                        {item.visits.current}/{item.visits.expectedCount}
                      </span>
                    </div>
                    {/* 5 Star */}
                    <div className={styles.metricsColumn}>
                      <div className={styles.starRating}>
                        <span>5</span>
                        <StarIcon />
                      </div>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${item.fiveStars.percent}%`,
                            backgroundColor: getProgressBarColors(item.status),
                          }}
                        />
                        <span className={styles.progressValue}>
                          {item.fiveStars.percent}%
                        </span>
                      </div>
                      <span className={styles.metricCount}>
                        {item.fiveStars.current}/{item.fiveStars.expectedCount}
                      </span>
                    </div>

                    {/* 4 Star */}
                    <div className={styles.metricsColumn}>
                      <div className={styles.starRating}>
                        <span>4</span>
                        <StarIcon />
                      </div>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${item.fourStars.percent}%`,
                            backgroundColor: getProgressBarColors(item.status),
                          }}
                        />
                        <span className={styles.progressValue}>
                          {item.fourStars.percent}%
                        </span>
                      </div>
                      <span className={styles.metricCount}>
                        {item.fourStars.current}/{item.fourStars.expectedCount}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return <>{renderOrderContent()}</>;
};

const OrderDetails = React.memo(OrderDetailsComponent);
export default OrderDetails;
